import { Box, Stack } from '@mui/material'
import { ArrowUpRightIcon, BlueprintIcon, FilesIcon, HighlighterIcon } from '@phosphor-icons/react'
import {
    assembleAddressFormat,
    Breadcrumbs,
    Footer,
    isoDateStringToLocalizedMonthYear,
    joinDefinedStrings,
    NavTab,
    PageGridContainer,
    PageGridItem,
    TabBar,
    TextNavButtonNew,
    TinyNavButton,
    TitleImage,
    tokens,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text/TextWithEllipsis/TextWithEllipsis'
import { Outlet, useLocation } from 'react-router-dom'
import { useAssetPageLayout } from './AssetPageLayout.data'
import AssetPageLayoutLoading from './AssetPageLayout.load'
import { getLatestUrlParams } from 'ComponentLibrary/src/hooks/useUrlState'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export default function AssetPageLayout() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tAssetPageLayout } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.assetPageLayout' })
    const locale = useCurrentResolvedLocale()
    const {
        asset,
        selectedTab,
        latestOrder,
        latestValuationOrValueCheck,
        latestUnlinkedDraft,
        latestUnlinkedDraftParentValuationStatus,
        showPlausibility,
        isAssetLayoutPending,
    } = useAssetPageLayout()

    const { street, house_number } = asset?.address ?? {}
    const addressLine = assembleAddressFormat({ street, house_number }, { delimiter: ', ' })
    const customerAssetReference = asset?.customer_asset_reference ? `[${asset?.customer_asset_reference}]` : undefined

    const orderDate = isoDateStringToLocalizedMonthYear(latestOrder?.valuation_date, 'long', locale)
    const valuationDate = isoDateStringToLocalizedMonthYear(
        latestValuationOrValueCheck?.valuation_date ?? latestValuationOrValueCheck?.created_on,
        'long',
        locale
    )
    const latestValuation =
        latestValuationOrValueCheck && 'valuation_type' in latestValuationOrValueCheck ? latestValuationOrValueCheck : undefined
    const isHistoricalValuation = latestValuation?.valuation_type === 'historical'
    const latestValuationUrl = latestValuationOrValueCheck
        ? latestValuation
            ? `valuations/${latestValuation.uuid}/overview`
            : `value-checks/${latestValuationOrValueCheck.uuid}`
        : undefined

    const shouldShowLatestDraftButton = showPlausibility && latestUnlinkedDraftParentValuationStatus === 'in_review'
    const latestDraftButtonHref = latestUnlinkedDraft ? `valuations/${latestUnlinkedDraft.uuid}/plausibility` : undefined

    const { pathname } = useLocation()
    const breadCrumbItems = [
        { title: tCommon('allAssets'), link: '/assets', params: getLatestUrlParams('client-asset-filter') },
        { title: tCommon('asset') },
    ]
    const breadcrumbs = <Breadcrumbs items={breadCrumbItems} currentLocation={pathname} />

    if (isAssetLayoutPending) {
        return <AssetPageLayoutLoading breadcrumbs={breadcrumbs} />
    }

    return (
        <Stack direction="column" minHeight="100%" bgcolor={tokens.ColorBackgroundDark} overflow="hidden">
            <Stack direction="column" gap={tokens.SizingSpacing300}>
                <PageGridContainer background={`linear-gradient(${tokens.ColorBackgroundLight} 194px, transparent 194px)`}>
                    <PageGridItem columnSpan={6} columnSpanXs={4}>
                        <Stack direction="row" paddingTop={tokens.SizingSpacing200} gap={tokens.SizingSpacing300} width="100%">
                            <Stack direction="column" gap={tokens.SizingSpacing400} width="max(570px, calc(100% - 390px))">
                                <Stack
                                    direction="column"
                                    gap={tokens.SizingSpacing300}
                                    paddingBottom={tokens.SizingSpacing075}
                                    width="100%"
                                >
                                    {breadcrumbs}
                                    <Stack
                                        direction="row"
                                        width="100%"
                                        gap={tokens.SizingSpacing400}
                                        height="120px"
                                        justifyContent="space-between"
                                    >
                                        <Stack direction="column" justifyContent="space-between">
                                            <Stack direction="column">
                                                <Typography variant="textM" color={tokens.ColorTextSubtle}>
                                                    {tAssetPageLayout('sectionLabel')}
                                                </Typography>
                                                <Stack
                                                    direction="row"
                                                    gap={tokens.SizingSpacing100}
                                                    height="40px"
                                                    alignItems="center"
                                                    maxWidth="max(570px, calc(100vw - 900px))"
                                                >
                                                    <TextWithEllipsis
                                                        tooltipText={joinDefinedStrings([addressLine, customerAssetReference])}
                                                        typographyProps={{ variant: 'headline1', color: tokens.ColorTextBoldest }}
                                                    >
                                                        {addressLine}
                                                        {customerAssetReference && (
                                                            <Box
                                                                display="inline-block"
                                                                marginLeft={tokens.SizingSpacing075}
                                                                sx={{ verticalAlign: 'text-bottom' }}
                                                            >
                                                                <Typography variant="headline3" color={tokens.ColorTextDefault} noWrap>
                                                                    {customerAssetReference}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </TextWithEllipsis>
                                                </Stack>
                                            </Stack>

                                            <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center" height="24px">
                                                {/*TODO: Include this once portfolio is exposed in the backend*/}
                                                {/*<FolderOpen color={tokens.ColorIconBoldest} size={tokens.SizingIconM} />*/}
                                                {/*<Typography variant="link" color={tokens.ColorTextLinkDefault} noWrap>*/}
                                                {/*    Portfolio Placeholder*/}
                                                {/*</Typography>*/}
                                            </Stack>
                                        </Stack>
                                        <Stack direction="column" alignItems="flex-end" alignSelf="flex-end">
                                            {shouldShowLatestDraftButton && latestUnlinkedDraft && latestDraftButtonHref && (
                                                <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                                    <Typography variant="textS" color={tokens.ColorTextSubtle} noWrap>
                                                        {tCommon('latestDraft')}
                                                    </Typography>
                                                    <TextNavButtonNew
                                                        label={tActions('viewPlausibility')}
                                                        leftIcon={<HighlighterIcon />}
                                                        rightIcon={<ArrowUpRightIcon />}
                                                        variant="primary"
                                                        href={latestDraftButtonHref}
                                                    />
                                                </Stack>
                                            )}
                                            {latestValuationOrValueCheck && !isHistoricalValuation && latestValuationUrl && (
                                                <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                                    <Typography variant="textS" color={tokens.ColorTextSubtle} noWrap>
                                                        {tCommon('latestValuation')}
                                                    </Typography>
                                                    <TinyNavButton
                                                        label={valuationDate}
                                                        leftIcon={<BlueprintIcon />}
                                                        rightIcon={<ArrowUpRightIcon />}
                                                        href={latestValuationUrl}
                                                    />
                                                </Stack>
                                            )}

                                            {latestOrder && (
                                                <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                                    <Typography variant="textS" color={tokens.ColorTextSubtle} noWrap>
                                                        {tCommon('latestOrder')}
                                                    </Typography>
                                                    <TinyNavButton
                                                        label={orderDate}
                                                        leftIcon={<FilesIcon />}
                                                        rightIcon={<ArrowUpRightIcon />}
                                                        href={`/orders/${latestOrder?.uuid}/overview`}
                                                    />
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Stack>

                                <TabBar selectedTab={selectedTab}>
                                    <NavTab label={tCommon('keyFigures')} value="overview" href="overview" />
                                    <NavTab label={tCommon('valuationHistory')} value="valuations" href="valuations" />
                                    <NavTab label={tCommon('deviationAnalysis')} value="deviation-analysis" href="deviation-analysis" />
                                </TabBar>
                            </Stack>

                            <TitleImage
                                width={366}
                                height={241}
                                image={
                                    asset?.title_image
                                        ? {
                                              src: asset.title_image,
                                              alt: tCommon('assetImageAlt'),
                                          }
                                        : undefined
                                }
                            />
                        </Stack>
                    </PageGridItem>
                </PageGridContainer>

                <Outlet />
            </Stack>

            <Footer withTopSpace />
        </Stack>
    )
}
